import { ReactComponent as AttachedIcon } from 'assets/attach-icon.svg'
import Input from 'components/Input'
import Button from 'components/UI/Button'
import { ReactElement, useState } from 'react'
import { wsApi } from 'redux/api/wsApi'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { Page, pageActions } from 'redux/slices/page'
import { Box, Flex, Text } from 'theme-ui'
import md5 from 'tiny-js-md5'
import { getCookieByName } from 'utils/cookie'

const containerPaddingX = '15px'

export default function SubmitTicketView(): ReactElement {
  const dispatch = useAppDispatch()
  const { messages, confidenceRatings, conversationId } = useAppSelector(x => x.message)
  const { issueType } = useAppSelector(x => x.issue)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [comments, setComments] = useState('')
  const [sendSupportTicket, sendSupportTicketResponse] = wsApi.useSendSupportTicketMutation()
  const shouldDisplayErrorMessage = sendSupportTicketResponse.isError

  const confidendeAvg =
    confidenceRatings.reduce((a, currentValue) => a + currentValue, 0) / confidenceRatings.length

  //retrieving this again here since the user might have logged in after the page was loaded
  const sessionAuth = getCookieByName('ws_session_auth_hash')

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        if (!email) {
          return
        }
        const time = new Date().getTime()
        const epochTime = Math.round(time / 1000).toString()
        const authHash = md5(process.env.REACT_APP_CLIENT_AUTH_SECRET + epochTime)

        sendSupportTicket({
          client_auth_hash: authHash,
          time: epochTime,
          support_email: email,
          ...(username && { support_name: username }),
          support_message: comments,
          issue_metadata: {
            chatlog: messages,
            confidence: confidendeAvg,
            garry_version: 2,
          },
          support_subject: `Garry Support Ticket from: ${email}`,
          support_category: 'garry',
          ...(sessionAuth && { session_auth_hash: sessionAuth }),
          conversation_id: conversationId,
        })
      }}
      sx={{ width: '100%', height: '100%' }}
    >
      <Flex
        data-testid="triage-view"
        sx={{
          width: '100%',
          height: '100%',
          flexDirection: 'column',
          alignItems: 'center',
          px: containerPaddingX,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ width: 'inherit' }}>
          <Button
            sx={{
              width: '100%',
              backgroundColor: 'neonGreen25',
              height: '48px',
              border: ({ colors }) => `2px solid ${colors?.neonGreen50}`,
              borderRadius: '10px',
              pointerEvents: 'none',
              mt: [0, '16px'],
            }}
            variant="primary"
            data-testid="chatlog-attached-button"
          >
            <Flex sx={{ justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
              <AttachedIcon />
              <Text sx={{ fontSize: '14px', color: 'neonGreen', fontWeight: 'bold' }}>
                Chatlog Attached
              </Text>
            </Flex>
          </Button>
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <h1 sx={{ color: 'white', mt: ['32px', '24px'], mb: 0 }}>Talk to a human</h1>
            <span
              sx={{
                color: shouldDisplayErrorMessage ? 'red' : 'white',
                mt: '16px',
                mb: 0,
                maxWidth: '296px',
                opacity: shouldDisplayErrorMessage ? 1 : 0.5,
                fontSize: '18px',
                fontWeight: 'normal',
                textAlign: 'center',
              }}
            >
              {shouldDisplayErrorMessage
                ? 'Something went wrong, please try sending it again.'
                : "Fill this form out and we'll get back to you as soon as possible by email"}
            </span>
          </Flex>
          <Box sx={{ mt: ['24px', '16px'], width: '100%' }}>
            <Text
              as="label"
              sx={{ ml: '15px', fontSize: '16px', fontWeight: 'bold' }}
              color="white50"
            >
              Username
            </Text>
            <Input
              containerSx={{ mt: '8px', mb: '16px' }}
              inputTestId="ticket_flow_username"
              // autoComplete="username"
              onChange={({ target }) => setUsername(target.value)}
              value={username}
              name="username"
              type="text"
            />

            <Text
              as="label"
              sx={{ ml: '15px', fontSize: '16px', fontWeight: 'bold' }}
              color="white50"
            >
              Email
            </Text>
            <Input
              containerSx={{ mt: '8px', mb: '16px' }}
              inputTestId="ticket_flow_email"
              type="email"
              autoComplete="email"
              required={true}
              onChange={({ target }) => setEmail(target.value)}
              value={email}
              name="email"
            />

            <Flex>
              <Text
                as="label"
                sx={{ ml: '15px', fontSize: '16px', fontWeight: 'bold' }}
                color="white50"
              >
                Comments
              </Text>
              <Text sx={{ ml: '3px', fontSize: '16px' }} color="white50">
                (Optional)
              </Text>
            </Flex>
            <Input
              containerSx={{ mt: '8px' }}
              inputTestId="ticket_flow_comments"
              name="comments"
              value={comments}
              onChange={({ target }) => setComments(target.value)}
            />
          </Box>
        </Box>
        <Box sx={{ width: '100%' }}>
          <hr
            sx={{
              borderColor: 'white15',
              mx: `-${containerPaddingX}`,
              my: 0,
              display: ['block', 'none'],
            }}
          />
          <Flex sx={{ justifyContent: 'space-between', my: ['12px', '18px'] }}>
            <Button
              variant="text"
              styles={{
                background: 'transparent',
                cursor: 'pointer',
                width: '203px',
                fontWeight: 'bold',
                color: 'white50',
                fontSize: '18px',
              }}
              onClick={() => dispatch(pageActions.switchPage(Page.CHAT_PAGE))}
              type="button"
            >
              Back
            </Button>
            <Button
              variant="neon"
              color="black"
              styles={{ fontWeight: 'bold', width: '203px' }}
              type="submit"
              data-testid="ticket_flow_submit"
              isLoading={sendSupportTicketResponse.isLoading}
            >
              Send
            </Button>
          </Flex>
        </Box>
      </Flex>
    </form>
  )
}
